import React, { useState, useMemo, useRef } from 'react';
import { Typography, withStyles, Tooltip, Link } from '@material-ui/core';
import {
  Column,
  Selection,
  Sorting
} from 'devextreme-react/data-grid';
import DataGrid from 'components/devExtreme/DataGrid';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { setSelectedValuation, setSelectedContainerId, takeValuation } from 'store/actions';
import classNames from 'classnames';
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';
import { Tooltip as DevExTooltip } from 'devextreme-react/tooltip';
import columnSchema from 'components/underwrite/acquisition/propertyUnderwrite/header/propertyHistory/valuationHistoryColumnSchema';
import { on } from 'devextreme/events';
import { format } from 'date-fns';
import { Constants } from 'utilities/constants';

// We'll remove withRouter if it's not needed for this grid's functionality
// import { withRouter } from 'react-router-dom';

const styles = theme => ({
  root: {
    minHeight: 152 // Keep consistent height
  },
  title: {
    marginBottom: 6,
  },
  grid: {
    maxHeight: 175 // Keep consistent height
  },
  link: {
    background: 'none',
    border: 'none',
    color: 'blue',
    textDecoration: 'underline',
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    '&:hover': {
      color: 'darkblue'
    }
  },
  disabledLink: {
    cursor: 'default',
    '&:hover': {
      textDecoration: 'none'
    }
  }
});

let lastRowId = null;
let rowShaded = false;

const PropertyHistoryValuationHistoryGrid = ({ classes, user, valuations, selectedProformaInputsContainerId,
  selectedProformaInputsContainerValuations, selectedValuation,
  setSelectedValuation, setSelectedContainerId, takeValuation, valuationIsDirty, propertyUWClientTxnOpp }) => {
  const [cellTooltip, setCellTooltip] = useState(null);
  const { openSaveUWDialog } = useSaveUWDialog();

  const cellTooltipRef = useRef(null);

  // No special cell preparation needed for now, as there are no warning/color rules.
  // const onCellPrepared = cell => { ... }

  // No special cell renderer needed for notes unless specified later
  // const renderValuationNotesCell = (cellInfo) => { ... }

  const onViewLinkClick = (row) => { // Renamed for clarity
    const isSameContainerId = selectedProformaInputsContainerId === row.containerID;
    if (isSameContainerId) {
      const foundValuation = selectedProformaInputsContainerValuations.find(val => val.id === row.id);
      if (foundValuation) {
        setSelectedValuation(foundValuation);
      }
    } else {
      if (valuationIsDirty) {
        openSaveUWDialog({ callback: () => {
          setSelectedContainerId(row.containerIsLatest ? null : row.containerID, row.id);
        }});
      } else {
        setSelectedContainerId(row.containerIsLatest ? null : row.containerID, row.id);
      }
    }
  };

  const onTakeLinkClick = (row) => { // Renamed for clarity
    if (valuationIsDirty) {
      openSaveUWDialog({ callback: () => {
        takeValuation(row.valuation, row.unmappedValuationsComps);
      }});
    } else {
      takeValuation(row.valuation, row.unmappedValuationsComps);
    }
  };

  // Custom cell render function for the "View" column
  const renderViewLink = (cellInfo) => {
    const isCurrentValuation = cellInfo.data.id === selectedValuation?.id;
    return (
      <Link
        component="button" // Treat as a button for accessibility/focus
        variant="body2" // Use a typography variant for text styling
        className={classNames(isCurrentValuation && classes.disabledLink)} // Apply custom link styles
        onClick={() => onViewLinkClick(cellInfo.data)} // Pass the row data
        disabled={isCurrentValuation}
        color={isCurrentValuation ? 'initial' : 'primary'}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}

      >
        View
      </Link>
    );
  };

  // Custom cell render function for the "Take" column
  const renderTakeLink = (cellInfo) => {
    const isUsersAndLatest = cellInfo.data.userId === user.id && cellInfo.data.containerIsLatest;
    const isAutoCalc = cellInfo.data.valuation.user.name === Constants.Username.AUTOCALC;
    const postAcq = propertyUWClientTxnOpp && propertyUWClientTxnOpp.isPostAcquisitionUnderwrite ? true : false;
    const disabled = isUsersAndLatest || postAcq || isAutoCalc;
    return (
      <Link
        component="button" // Treat as a button for accessibility/focus
        variant="body2"
        className={classNames(disabled && classes.disabledLink)} // Apply custom link styles
        onClick={() => onTakeLinkClick(cellInfo.data)} // Pass the row data
        color={disabled ? 'initial' : 'primary'}
        disabled={disabled}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}
      >
        Take
      </Link>
    );
  };

  const renderValuationNotesCell = (cellInfo) => {
    const text = cellInfo.value || '';  
  
    const ellipsisStyle = {
      whiteSpace:    'nowrap',
      overflow:      'hidden',
      textOverflow:  'ellipsis',
      display:       'block',
      width:         '100%',
      cursor:        'help'
    };
  
    return (
      <Tooltip title={text} arrow>
        <span style={ellipsisStyle}>
          {text}
        </span>
      </Tooltip>
    );
  };

  const renderValuationUserCell = (cellInfo) => {
    const isDotted = cellInfo.data.id === 0;
    const isRed = cellInfo.data.valuation?.valuationOutput?.isSuccess === false;
    const cellStyle = {};
    if (isDotted) {
      cellStyle.outline = '1px dotted gray';
      cellStyle.padding = '0 2px';
      cellStyle.display = 'inline-block';
    }
    if (isRed) {
      cellStyle.color = 'red';
    }
    return (
      <span style={cellStyle}>
        {cellInfo.value}
      </span>
    );
  };

  const onRowPrepared = e => {
    if (e.rowType === "data") {
      if (e.data.containerID !== lastRowId) {
        lastRowId = e.data.containerID;
        rowShaded = !rowShaded;
        if (e.rowIndex !== 0) e.rowElement.style.borderTop = '2px solid #999999';
      }
    }
  }

  const onCellPrepared = e => {
    if (e.rowType === 'data' && e.column.dataType === 'date' && e.value) {
      on(e.cellElement, 'mouseover', async(mouseEvent) => {
        setCellTooltip(format(e.value, 'MM/dd/yyyy, h:mm:ss a'));
        await cellTooltipRef.current?.instance.show(mouseEvent.target);
      });

      on(e.cellElement, 'mouseout', async() => {
        await cellTooltipRef.current?.instance.hide();
      });
    }
  }

  let tooltips = useMemo(() => {
    return columnSchema.filter(col => col.tooltip != null).map(({
      field,
      displayName,
      tooltip
    }) => {
      const target = `div#valuationHistoryGrid td[aria-label='Column ${displayName}']`;
      return (
        <DevExTooltip
          key={field}
          target={target}
          showEvent="dxhoverstart"
          hideEvent="dxhoverend"
          position="top"
        >
          <p>{tooltip}</p>
        </DevExTooltip>
      )
    })
  }, []);

  return (
    <div className={classes.root}>
      {
        valuations && valuations?.length > 0 ?
          <>
            <DataGrid
              id={'valuationHistoryGrid'}
              dataSource={valuations}
              showBorders={true}
              showRowLines={true}
              showColumnLines={true}
              hoverStateEnabled={true}
              allowColumnResizing={true}
              className={classes.grid}
              columnAutoWidth={true}
              onCellPrepared={onCellPrepared}
              width='100%'
              height={152} // Keeping a fixed height, adjust if needed
              onRowPrepared={onRowPrepared}
              enableTooltips
              tooltips={columnSchema}
            >
              <Sorting mode={'multiple'} />
              <Selection mode='single' />
              <Column
                caption=""
                cellRender={renderViewLink} // Use the link renderer
                width={60}
                allowSorting={false}
                allowFiltering={false}
                fixed={true}
              />
              <Column
                caption=""
                cellRender={renderTakeLink} // Use the link renderer
                width={60}
                allowSorting={false}
                allowFiltering={false}
                fixed={true}
              />
              {columnSchema.map(({ field, displayName, width, type, format, sortIndex, sortOrder }) => {
                return <Column
                  key={field}
                  dataField={field}
                  caption={displayName}
                  width={width}
                  dataType={type}
                  format={format}
                  sortIndex={sortIndex}
                  sortOrder={sortOrder}
                  cellRender={field === 'notes' ? renderValuationNotesCell : 
                                field === 'valuationUserFullName' ? renderValuationUserCell : null}
                />
              })}
            </DataGrid>
            {/* {tooltips} */}
            <DevExTooltip
              ref={cellTooltipRef}
              position="top"
            >
              <p>{cellTooltip}</p>
            </DevExTooltip>
          </>
          :
          <Typography className={classes.title} variant="subtitle2">
            No valuations
          </Typography>
      }
    </div>
  );
};

const mapStateToProps = ({ appState, propertyUWState }) => {
  return {
    user: appState.user,
    selectedValuation: propertyUWState.selectedValuation,
    selectedProformaInputsContainerId: propertyUWState.selectedProformaInputsContainer?.id,
    selectedProformaInputsContainerValuations: propertyUWState.selectedProformaInputsContainer?.valuations,
    valuationIsDirty: propertyUWState.valuationIsDirty,
    propertyUWClientTxnOpp: propertyUWState.propertyUWClientTxnOpp
  }
};

const mapDispatchToProps = {
  setSelectedValuation,
  setSelectedContainerId,
  takeValuation
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyHistoryValuationHistoryGrid);